/**
 * Instituições mais comuns para o cadastro de conta. O nome escolhido vira o texto de `institution`
 * (a API não mudou); o logo é procurado pelo `slug` (ver `bancosLogos.ts` em cada app).
 * `cor` é a cor da marca, usada no selo com as iniciais enquanto não há logo baixado.
 * Cópia idêntica em Web/src/lib/bancos.ts — o Web mantém as próprias cópias (ver shared/README.md).
 */
export interface Banco {
  slug: string
  nome: string
  cor: string
  /** Código de compensação do Banco Central (COMPE), quando houver. */
  codigo?: string
  /** Outras grafias que o usuário pode digitar. */
  aliases?: string[]
}

export const BANCOS: Banco[] = [
  { slug: 'itau', nome: 'Itaú', cor: '#EC7000', codigo: '341', aliases: ['itau unibanco', 'itaú unibanco'] },
  { slug: 'nubank', nome: 'Nubank', cor: '#820AD1', codigo: '260', aliases: ['nu pagamentos', 'nu bank', 'roxinho'] },
  { slug: 'bradesco', nome: 'Bradesco', cor: '#CC092F', codigo: '237' },
  { slug: 'santander', nome: 'Santander', cor: '#EC0000', codigo: '033' },
  { slug: 'banco-do-brasil', nome: 'Banco do Brasil', cor: '#FFEF38', codigo: '001', aliases: ['bb'] },
  { slug: 'caixa', nome: 'Caixa Econômica Federal', cor: '#005CA9', codigo: '104', aliases: ['caixa', 'cef'] },
  { slug: 'inter', nome: 'Banco Inter', cor: '#FF7A00', codigo: '077', aliases: ['inter'] },
  { slug: 'c6', nome: 'C6 Bank', cor: '#242424', codigo: '336', aliases: ['c6'] },
  { slug: 'btg', nome: 'BTG Pactual', cor: '#0B2D5B', codigo: '208', aliases: ['btg'] },
  { slug: 'xp', nome: 'XP Investimentos', cor: '#FFD400', codigo: '348', aliases: ['xp', 'banco xp'] },
  { slug: 'picpay', nome: 'PicPay', cor: '#21C25E', codigo: '380' },
  { slug: 'mercado-pago', nome: 'Mercado Pago', cor: '#00B1EA', codigo: '323' },
  { slug: 'pagbank', nome: 'PagBank', cor: '#00B140', codigo: '290', aliases: ['pagseguro'] },
  { slug: 'original', nome: 'Banco Original', cor: '#00A859', codigo: '212', aliases: ['original'] },
  { slug: 'next', nome: 'Next', cor: '#00E06E' },
  { slug: 'neon', nome: 'Neon', cor: '#00C2D1' },
  { slug: 'will-bank', nome: 'Will Bank', cor: '#FFE500', codigo: '280', aliases: ['will'] },
  { slug: 'safra', nome: 'Banco Safra', cor: '#003A8C', codigo: '422', aliases: ['safra'] },
  { slug: 'sicredi', nome: 'Sicredi', cor: '#3FA110', codigo: '748' },
  { slug: 'sicoob', nome: 'Sicoob', cor: '#003641', codigo: '756' },
  { slug: 'banrisul', nome: 'Banrisul', cor: '#0066B3', codigo: '041' },
  { slug: 'brb', nome: 'BRB', cor: '#0057A8', codigo: '070', aliases: ['banco de brasilia'] },
  { slug: 'banco-pan', nome: 'Banco Pan', cor: '#0A6CDB', codigo: '623', aliases: ['pan'] },
  { slug: 'bmg', nome: 'Banco BMG', cor: '#F26B21', codigo: '318', aliases: ['bmg'] },
  { slug: 'agibank', nome: 'Agibank', cor: '#00A0DF', codigo: '121', aliases: ['agi'] },
  { slug: 'digio', nome: 'Digio', cor: '#0068FF', codigo: '335' },
  { slug: 'bv', nome: 'Banco BV', cor: '#0F4CFF', codigo: '413', aliases: ['votorantim', 'bv'] },
  { slug: 'daycoval', nome: 'Banco Daycoval', cor: '#00A3E0', codigo: '707', aliases: ['daycoval'] },
  { slug: 'mercantil', nome: 'Banco Mercantil do Brasil', cor: '#00549F', codigo: '389', aliases: ['mercantil'] },
  { slug: 'modal', nome: 'Banco Modal', cor: '#1A1A1A', codigo: '746', aliases: ['modalmais', 'modal'] },
  { slug: 'genial', nome: 'Banco Genial', cor: '#00B5AD', codigo: '125', aliases: ['genial'] },
  { slug: 'stone', nome: 'Stone', cor: '#00A868', codigo: '197' },
  { slug: 'cora', nome: 'Cora', cor: '#FE3E6D', codigo: '403' },
  { slug: 'banestes', nome: 'Banestes', cor: '#0069B4', codigo: '021' },
  { slug: 'banco-do-nordeste', nome: 'Banco do Nordeste', cor: '#E30613', codigo: '004', aliases: ['bnb'] },
  { slug: 'banco-da-amazonia', nome: 'Banco da Amazônia', cor: '#00843D', codigo: '003', aliases: ['basa'] },
  { slug: 'unicred', nome: 'Unicred', cor: '#00A551', codigo: '136' },
  { slug: 'ailos', nome: 'Ailos', cor: '#00A0DF', codigo: '085' },
  { slug: 'cresol', nome: 'Cresol', cor: '#00A94F', codigo: '133' },
  { slug: 'revolut', nome: 'Revolut', cor: '#0666EB' },
  { slug: 'nomad', nome: 'Nomad', cor: '#FFD500' },
  { slug: 'wise', nome: 'Wise', cor: '#9FE870' },
  { slug: 'iti', nome: 'iti', cor: '#FF6B00' },
  { slug: 'recargapay', nome: 'RecargaPay', cor: '#00C46A' },
  { slug: 'infinitepay', nome: 'InfinitePay', cor: '#00E676' },
  { slug: 'ame', nome: 'Ame Digital', cor: '#E6007E' },
  { slug: 'rico', nome: 'Rico', cor: '#FF5A1F' },
  { slug: 'clear', nome: 'Clear Corretora', cor: '#00A3AD', aliases: ['clear'] },
  { slug: 'nuinvest', nome: 'NuInvest', cor: '#820AD1', aliases: ['easynvest', 'nu invest'] },
]

/** Minúsculas, sem acento e sem pontuação — base de toda comparação de nomes. */
export function normalizarNome(texto: string): string {
  return texto.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim()
}

const INDICE = BANCOS.map((b) => ({
  banco: b,
  nomes: [b.nome, b.slug.replace(/-/g, ' '), ...(b.aliases ?? [])].map(normalizarNome),
}))

/** Filtra pelo que foi digitado (nome, apelido ou código). Quem começa com o texto vem primeiro. */
export function buscarBancos(consulta: string): Banco[] {
  const q = normalizarNome(consulta)
  if (!q) return BANCOS
  const pontuados: { banco: Banco; ordem: number }[] = []
  for (const { banco, nomes } of INDICE) {
    let ordem = -1
    if (nomes.some((n) => n.startsWith(q))) ordem = 0
    else if (nomes.some((n) => n.split(' ').some((p) => p.startsWith(q)))) ordem = 1
    else if (nomes.some((n) => n.includes(q)) || banco.codigo === q) ordem = 2
    if (ordem >= 0) pontuados.push({ banco, ordem })
  }
  return pontuados.sort((a, b) => a.ordem - b.ordem).map((p) => p.banco)
}

/** Reconhece o banco a partir do texto já salvo na conta ("Itaú", "itau unibanco"...). Sem palpite: só acerta o nome inteiro ou um apelido. */
export function acharBanco(texto: string | undefined | null): Banco | undefined {
  if (!texto) return undefined
  const q = normalizarNome(texto)
  if (!q) return undefined
  return INDICE.find(({ nomes }) => nomes.includes(q))?.banco
}

/** Até duas letras para o selo quando não há logo. */
export function iniciaisDoBanco(nome: string): string {
  const todas = normalizarNome(nome).split(' ').filter((p) => p.length > 1 || /\d/.test(p))
  const uteis = todas.filter((p) => !['banco', 'bco', 'do', 'da', 'de', 'dos', 'das'].includes(p))
  const partes = uteis.length ? uteis : todas
  const letras = partes.length > 1 ? partes[0][0] + partes[1][0] : (partes[0] ?? nome).slice(0, 2)
  return letras.toUpperCase()
}

/** Preto ou branco, conforme o contraste sobre a cor da marca. */
export function corDoTextoSobre(hex: string): string {
  const n = parseInt(hex.slice(1), 16)
  const luz = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255
  return luz > 0.6 ? '#101319' : '#FFFFFF'
}
